import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  DialogTitle,
  Divider,
  Stack,
} from "@mui/material";
import {
  CloseIconWrapper,
  CreateBoardWrapper,
  FontGrayNormal,
  ModalIconWrapper,
  ModalTitle,
  StyledContainedButton,
  StyledDialog,
  StyledFormControl,
  StyledInputLabel,
  StyledSelectField,
  StyledTextField,
} from "./style";
import { CloseIcon, CreateFolderIcon } from "../../assets/Icons";
import { CreateTeamFolderValueProps } from "../../utils/propTypes";
import { useDispatch, useSelector } from "react-redux";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ErrorMessage } from "../../pageComponents/auth/style";
import {
  folderItemSelector,
  isLoadingSelector,
  myTeamListSelector,
  openCreateTeamFolderSelector,
} from "../../store/metadata/selector";
import { MetaDataReducerAction } from "../../store/metadata/slice";
import { webkitInputCSSFix } from "../../utils/helpers";
import { useEffect } from "react";
import { StyledMenuItem } from "../../globalStyle";

export const createTeamFolderValidationSchema = (isBoardEdit: boolean) =>
  yup.object().shape({
    name: yup
      .string()
      .required("Folder Name is Required!")
      .max(25, "Folder name must not exceed 25 characters"),
    teamIds: isBoardEdit
      ? yup.array().optional()
      : yup
          .array()
          .of(yup.string().required())
          .min(1, "Select at least one team")
          .required("Team selection is required"),
  });

const CreateTeamFolderModal = () => {
  const dispatch = useDispatch();
  const folderItem = useSelector(folderItemSelector);
  const loading = useSelector(isLoadingSelector);
  const openModal = useSelector(openCreateTeamFolderSelector);
  const myTeamList = useSelector(myTeamListSelector) || [];
  const isBoardEdit = !!folderItem?.selectedBoard;

  const {
    getValues,
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = useForm<CreateTeamFolderValueProps>({
    defaultValues: {
      name: folderItem?.selectedBoard?.name || folderItem?.name,
      teamIds: folderItem?.teamIds || [],
    },
    resolver: yupResolver(createTeamFolderValidationSchema(isBoardEdit)),
  });

  useEffect(() => {
    dispatch(MetaDataSagaActions.getMyteamsList());
  }, [dispatch]);

  const onSubmit = () => {
    const values = getValues();

    if (folderItem?.selectedBoard) {
      dispatch(
        MetaDataSagaActions.updateBoard({
          boardId: folderItem?.selectedBoard?._id,
          name: values.name,
          folderId: folderItem?.selectedBoard?.folderId,
          resetState: handleCloseModal,
        })
      );
    } else if (folderItem) {
      dispatch(
        MetaDataSagaActions.updateFolder({
          name: values.name,
          folderId: folderItem._id,
          teamIds: values?.teamIds,
          teamFolder: true,
          resetState: handleCloseModal,
        })
      );
    } else {
      dispatch(
        MetaDataSagaActions.createTeamFolder({
          name: values.name,
          teamIds: values?.teamIds,
          resetState: handleCloseModal,
        })
      );
    }
  };

  const handleCloseModal = () => {
    dispatch(MetaDataReducerAction.setOpenTeamCreateFolder(false));
    dispatch(MetaDataReducerAction.setFolderItem(null));
  };

  return (
    <StyledDialog
      onClose={handleCloseModal}
      open={openModal}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ padding: "1.875rem 1.875rem 1.25rem 1.875rem" }}>
        <Stack direction="row" gap="0.75rem" alignItems="start">
          <ModalIconWrapper>
            <CreateFolderIcon fill="white" />
          </ModalIconWrapper>
          <Stack>
            <ModalTitle>
              {folderItem?.selectedBoard
                ? "Update Board"
                : folderItem
                ? "Update Team Folder"
                : "Create Team Folder"}
            </ModalTitle>
            <FontGrayNormal>
              Keep boards organized with folders shared across your team.
            </FontGrayNormal>
          </Stack>
        </Stack>
        <CloseIconWrapper aria-label="close" onClick={handleCloseModal}>
          <CloseIcon />
        </CloseIconWrapper>
      </DialogTitle>
      <Divider sx={{ borderColor: "#E3E6EA" }} />
      <CreateBoardWrapper>
        <StyledFormControl fullWidth>
          <StyledInputLabel htmlFor="name">
            {folderItem?.selectedBoard ? "Board Name" : "Folder Name"}
          </StyledInputLabel>
          <StyledTextField
            id="name"
            variant="outlined"
            {...register("name", { required: true })}
            sx={webkitInputCSSFix}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit(onSubmit)();
              }
            }}
          />
          {errors.name?.message && (
            <ErrorMessage>{errors.name?.message}</ErrorMessage>
          )}
        </StyledFormControl>
        {folderItem?.selectedBoard ? null : (
          <StyledFormControl fullWidth>
            <StyledInputLabel htmlFor="teamIds" shrink={false}>
              Teams
            </StyledInputLabel>
            <Controller
              name="teamIds"
              control={control}
              render={({ field }) => (
                <StyledSelectField
                  id="teamIds"
                  multiple
                  value={field.value || []}
                  onChange={(e) => field.onChange(e.target.value)}
                  inputProps={{
                    id: "select-multiple-chip",
                  }}
                  renderValue={(value) => {
                    const selected = value as string[];
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((id) => {
                          const team = myTeamList?.find(
                            (t: any) => t._id === id
                          );
                          if (!team) return null;

                          return (
                            <Chip
                              key={id}
                              label={team.name}
                              onMouseDown={(e) => e.stopPropagation()}
                              onDelete={() => {
                                const updated = selected.filter(
                                  (item) => item !== id
                                );
                                field.onChange(updated);
                              }}
                            />
                          );
                        })}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        borderRadius: 15,
                        maxHeight: "300px",
                        overflowY: "auto",
                      },
                    },
                  }}
                >
                  {myTeamList && myTeamList.length > 0 ? (
                    myTeamList.map((team: any) => (
                      <StyledMenuItem key={team._id} value={team._id}>
                        {team.name}
                      </StyledMenuItem>
                    ))
                  ) : (
                    <StyledMenuItem disabled>No data available</StyledMenuItem>
                  )}
                </StyledSelectField>
              )}
            />
            {errors?.teamIds && (
              <ErrorMessage>{errors?.teamIds?.message}</ErrorMessage>
            )}
          </StyledFormControl>
        )}
      </CreateBoardWrapper>
      <Divider sx={{ borderColor: "#E3E6EA" }} />
      <StyledContainedButton
        variant="contained"
        size="small"
        sx={{
          margin: "1.875rem 1.875rem 1.875rem auto",
          padding: "0.313rem 2.375rem",
        }}
        onClick={handleSubmit(onSubmit)}
        disabled={loading}
        startIcon={loading && <CircularProgress size={20} color="inherit" />}
      >
        {folderItem?.selectedBoard
          ? "Update Board"
          : folderItem
          ? "Update Folder"
          : "Create Folder"}
      </StyledContainedButton>
    </StyledDialog>
  );
};

export default CreateTeamFolderModal;
